/**
 * SkillCenterPageV2 — proper v2 redesign of Skill Center (G111).
 *
 * Canonical inventory pattern (FacetRail + Table + Slideout).
 * Reuses every backend code path from v1 SkillCenterPage:
 *   - api.listSkillRegistry, searchSkillRegistry, listInstalledSkills,
 *     listManagedSkills (used to fetch the four stripes)
 *   - api.installSkill, disableSkill, publishWorkflowAsSkill (mutations)
 *
 * v1 stays as fallback via /skills?legacy=1.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNavigation } from '../contexts/NavigationContext';
import { useToast } from '../contexts/ToastContext';
import { isRebirthSkillCenterEnabled } from '../utils/featureFlags';
import * as api from '../services/api';
import {
  RefreshCw, Plus, Sparkles, ShoppingBag, Layers, Package, Clock,
  AlertTriangle, BarChart3, Search, X, Trash2, Settings, RotateCcw,
} from 'lucide-react';
import {
  Button, Pill, Card, Modal, Slideout, Table, FacetRail, EmptyState,
  MetricCard, TabNav,
} from '../components/v2';
import { Pagination, usePaginationState } from '../components/common/Pagination';

import SkillCenterPage from './SkillCenterPage';

const KIND_OPTIONS = [
  { key: 'connector_skill',     label: 'Connector skill', tone: 'cyan' },
  { key: 'connector_operation', label: 'Connector op',    tone: 'cyan' },
  { key: 'workflow_skill',      label: 'Workflow skill',  tone: 'trust' },
  { key: 'system_skill',        label: 'System skill',    tone: 'muted' },
  { key: 'autocreate_derived',  label: 'Auto-Create-derived', tone: 'action' },
  /* P0-2 — wasm_skill marketplace filter. Customers can browse for
   * sandboxed third-party skills published to the registry. The
   * shipped sandbox runtime (Chicory) enforces hard memory + time
   * caps and the host-import bridge audits every crossing. */
  { key: 'wasm_skill',          label: 'Wasm sandbox',    tone: 'success' },
];
const KIND_TONE = Object.fromEntries(KIND_OPTIONS.map(o => [o.key, o.tone]));

const RISK_OPTIONS = [
  { key: 'high',   label: 'High',   tone: 'danger' },
  { key: 'medium', label: 'Medium', tone: 'warning' },
  { key: 'low',    label: 'Low',    tone: 'success' },
];
const RISK_TONE = Object.fromEntries(RISK_OPTIONS.map(o => [o.key, o.tone]));

/* P7J-RUN-037 — Skill Unification Phase 3a UI. ORIGIN is the new
 * top-of-rail facet (per the approved mockup) that partitions the
 * Skill Center by provenance: vendor-published (MARKETPLACE), authored
 * via +New Skill (TENANT_AUTHORED), probed from an ExteLet's platform
 * connector (DISCOVERED), or auto-generated by an AutoLet pipeline
 * (AI_GENERATED). Tones picked to align with the mockup pills. */
const ORIGIN_OPTIONS = [
  { key: 'MARKETPLACE',     label: 'Marketplace',     tone: 'action'  },
  { key: 'TENANT_AUTHORED', label: 'Tenant authored', tone: 'success' },
  { key: 'DISCOVERED',      label: 'Discovered',      tone: 'warning' },
  { key: 'AI_GENERATED',    label: 'AI-generated',    tone: 'trust'   },
];
const ORIGIN_TONE  = Object.fromEntries(ORIGIN_OPTIONS.map(o => [o.key, o.tone]));
const ORIGIN_LABEL = Object.fromEntries(ORIGIN_OPTIONS.map(o => [o.key, o.label]));

/* P7J-RUN-037 — LIFECYCLE state surfaced as a sibling rail and an
 * inline row pill. RETIRED rows are still queryable but hidden by
 * default (the FE filter only sets RETIRED when the operator explicitly
 * picks it). DEPRECATED rows render with an amber pill so operators
 * know the upstream vendor recommends migrating off. */
const LIFECYCLE_OPTIONS = [
  { key: 'ACTIVE',     label: 'Active',     tone: 'success' },
  { key: 'DEPRECATED', label: 'Deprecated', tone: 'warning' },
  { key: 'RETIRED',    label: 'Retired',    tone: 'danger'  },
  { key: 'DRAFT',      label: 'Draft',      tone: 'muted'   },
];
const LIFECYCLE_TONE = Object.fromEntries(LIFECYCLE_OPTIONS.map(o => [o.key, o.tone]));

function SkillCenterPageV2Inner() {
  const navigate = useNavigate();
  const { recordEntity } = useNavigation();
  const { addToast } = useToast();
  const [marketplace, setMarketplace] = useState([]);
  const [installedIds, setInstalledIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  /* G198 — Add Skill picker. Skills live on an agent; the global Add Skill
   * action picks an agent then deep-links to its detail page Build tab with
   * ?intent=add-skill which auto-opens the skill-create modal there. */
  const [addSkillOpen, setAddSkillOpen] = useState(false);
  const [addSkillAgents, setAddSkillAgents] = useState([]);
  const [addSkillFilter, setAddSkillFilter] = useState('');
  const [addSkillLoading, setAddSkillLoading] = useState(false);
  const openAddSkill = async () => {
    setAddSkillOpen(true);
    setAddSkillFilter('');
    setAddSkillLoading(true);
    try {
      const res = await api.getAgents?.({});
      const arr = Array.isArray(res?.data?.items) ? res.data.items
                : Array.isArray(res?.data) ? res.data
                : Array.isArray(res?.items) ? res.items
                : Array.isArray(res) ? res : [];
      setAddSkillAgents(arr);
    } catch { setAddSkillAgents([]); }
    finally { setAddSkillLoading(false); }
  };

  /* ── Load: marketplace + installed + managed (wizard / pending) ─── */
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [mkt, inst] = await Promise.all([
        api.listSkillRegistry?.({ size: 200 })?.catch(() => null),
        api.listInstalledSkills?.({ size: 200 })?.catch(() => null),
      ]);
      const items = Array.isArray(mkt?.data?.items) ? mkt.data.items
                  : Array.isArray(mkt?.items) ? mkt.items
                  : Array.isArray(mkt) ? mkt : [];
      const installedItems = Array.isArray(inst?.data?.items) ? inst.data.items
                  : Array.isArray(inst?.items) ? inst.items
                  : Array.isArray(inst) ? inst : [];
      const installedSet = new Set(installedItems.map(i => i.id));
      setMarketplace(items);
      setInstalledIds(installedSet);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  /* ── Facets ──────────────────────────────────────────────────── */
  /* P7J-RUN-037 — `origin` + `lifecycleState` added (Phase 3a). Both
   * surface as in-memory facets — the server-side filter endpoint
   * (added in the same phase) is reserved for future server-paged
   * cases; the Skill Center's data set is small enough that filtering
   * client-side keeps the UX snappy with zero round-trips. */
  const [facets, setFacets] = useState(() => ({
    origin:         new Set(),
    lifecycleState: new Set(),
    kind:           new Set(),
    risk:           new Set(),
    category:       new Set(),
    status:         new Set(),  // installed / available
  }));
  const toggleFacet = (groupKey, optionKey, nextChecked) => {
    setFacets(prev => {
      const next = new Set(prev[groupKey] || []);
      if (nextChecked) next.add(optionKey); else next.delete(optionKey);
      return { ...prev, [groupKey]: next };
    });
  };
  const clearFacets = () => setFacets({
    origin: new Set(), lifecycleState: new Set(),
    kind: new Set(), risk: new Set(), category: new Set(), status: new Set(),
  });

  const categories = useMemo(() => {
    const set = new Set();
    for (const s of marketplace) if (s.category) set.add(s.category);
    return Array.from(set).sort();
  }, [marketplace]);

  const FACET_GROUPS = useMemo(() => ([
    /* P7J-RUN-037 — Origin + Lifecycle prepended so the most
     * meaningful provenance partitions sit at the top of the rail. */
    { key: 'origin',         label: 'Origin',    options: ORIGIN_OPTIONS.map(o => ({ key: o.key, label: o.label })) },
    { key: 'lifecycleState', label: 'Lifecycle', options: LIFECYCLE_OPTIONS.map(o => ({ key: o.key, label: o.label })) },
    { key: 'kind',     label: 'Kind',     options: KIND_OPTIONS.map(o => ({ key: o.key, label: o.label })) },
    { key: 'risk',     label: 'Risk',     options: RISK_OPTIONS.map(o => ({ key: o.key, label: o.label })) },
    { key: 'category', label: 'Category', options: categories.map(c => ({ key: c, label: c })) },
    { key: 'status',   label: 'Status',   options: [
      { key: 'installed', label: 'Installed' },
      { key: 'available', label: 'Available' },
    ]},
  ]), [categories]);

  const skillStatus = (s) => installedIds.has(s.id) ? 'installed' : 'available';

  const filtered = useMemo(() => {
    return marketplace.filter(s => {
      /* P7J-RUN-037 — Origin + Lifecycle predicates. Default to
       * MARKETPLACE for rows pre-V231 that don't carry the new column.
       * Lifecycle defaults to ACTIVE so legacy rows aren't hidden by
       * the implicit "hide RETIRED" rule below. */
      const sOrigin    = s.origin || 'MARKETPLACE';
      const sLifecycle = s.lifecycleState || 'ACTIVE';
      if (facets.origin.size > 0 && !facets.origin.has(sOrigin)) return false;
      if (facets.lifecycleState.size > 0 && !facets.lifecycleState.has(sLifecycle)) return false;
      // Implicit rule: when no lifecycle filter is set, hide RETIRED
      // entries (they're not actionable). Operators must explicitly
      // pick RETIRED in the rail to see them — same pattern the mockup
      // calls out under "Lifecycle controls". DEPRECATED stays visible
      // with an amber pill so consumers can plan migrations.
      if (facets.lifecycleState.size === 0 && sLifecycle === 'RETIRED') return false;

      // Fold the legacy 'runbook_derived' kind into the consolidated
      // 'autocreate_derived' bucket so one filter matches both.
      const sKind = s.kind === 'runbook_derived' ? 'autocreate_derived' : s.kind;
      if (facets.kind.size > 0 && !facets.kind.has(sKind)) return false;
      if (facets.risk.size > 0 && !facets.risk.has((s.riskLevel || s.risk || '').toLowerCase())) return false;
      if (facets.category.size > 0 && !facets.category.has(s.category)) return false;
      if (facets.status.size > 0 && !facets.status.has(skillStatus(s))) return false;
      return true;
    });
  }, [marketplace, facets, installedIds]);

  const facetCounts = useMemo(() => {
    const counts = {};
    for (const g of FACET_GROUPS) {
      counts[g.key] = {};
      for (const opt of g.options) {
        counts[g.key][opt.key] = marketplace.filter(s => {
          for (const og of FACET_GROUPS) {
            if (og.key === g.key) continue;
            const sel = facets[og.key];
            if (!sel || sel.size === 0) continue;
            if (og.key === 'risk') { if (!sel.has((s.riskLevel || s.risk || '').toLowerCase())) return false; }
            else if (og.key === 'status') { if (!sel.has(skillStatus(s))) return false; }
            /* P7J-RUN-037 — read from origin/lifecycleState with the
             * same legacy-row default applied at filter time, so the
             * count rail matches what the user actually sees. */
            else if (og.key === 'origin')         { if (!sel.has(s.origin || 'MARKETPLACE')) return false; }
            else if (og.key === 'lifecycleState') { if (!sel.has(s.lifecycleState || 'ACTIVE')) return false; }
            else if (!sel.has(s[og.key])) return false;
          }
          if (g.key === 'risk')   return (s.riskLevel || s.risk || '').toLowerCase() === opt.key;
          if (g.key === 'status') return skillStatus(s) === opt.key;
          if (g.key === 'origin')         return (s.origin || 'MARKETPLACE') === opt.key;
          if (g.key === 'lifecycleState') return (s.lifecycleState || 'ACTIVE') === opt.key;
          return s[g.key] === opt.key;
        }).length;
      }
    }
    return counts;
  }, [marketplace, facets, FACET_GROUPS, installedIds]);

  const facetGroupsWithCounts = useMemo(() => (
    FACET_GROUPS.map(g => ({
      ...g,
      options: g.options.map(o => ({
        ...o,
        count: facetCounts[g.key]?.[o.key] || 0,
        checked: facets[g.key]?.has(o.key) || false,
      })),
    }))
  ), [facetCounts, facets, FACET_GROUPS]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS) {
      for (const optKey of facets[g.key] || []) {
        const opt = g.options.find(o => o.key === optKey);
        chips.push({ groupKey: g.key, optionKey: optKey, label: `${g.label}: ${opt?.label || optKey}` });
      }
    }
    return chips;
  }, [facets, FACET_GROUPS]);

  /* Search input */
  const [searchQuery, setSearchQuery] = useState('');
  const searched = useMemo(() => {
    if (!searchQuery.trim()) return filtered;
    const q = searchQuery.trim().toLowerCase();
    return filtered.filter(s =>
      (s.name || s.displayName || '').toLowerCase().includes(q) ||
      (s.description || '').toLowerCase().includes(q) ||
      (s.kind || '').toLowerCase().includes(q) ||
      (s.category || '').toLowerCase().includes(q)
    );
  }, [filtered, searchQuery]);

  /* G175 — Pagination over the searched/filtered list. Reset to page 1 whenever
   * the searched length changes so the user doesn't land on an empty page. */
  const pg = usePaginationState(10);
  const visibleRows = pg.paginate(searched);
  useEffect(() => { pg.setPage(0); }, [searched.length]);

  /* Density */
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem('skills-density') || 'comfortable'; }
    catch { return 'comfortable'; }
  });
  const cycleDensity = () => {
    const next = density === 'comfortable' ? 'compact' : 'comfortable';
    setDensity(next);
    try { localStorage.setItem('skills-density', next); } catch {}
  };

  /* Slideout */
  const [selected, setSelected] = useState(null);
  const [slideoutTab, setSlideoutTab] = useState('overview');
  const openSlideout = (row) => {
    setSelected(row); setSlideoutTab('overview');
    // Record this skill in the recent-history trail (top-bar "Recent").
    const nm = row?.displayName || row?.name;
    if (nm) recordEntity({ type: 'Skill', id: row?.id, name: nm, path: '/skill-center' });
  };
  const closeSlideout = () => setSelected(null);

  /* P7J-RUN-037 — Skill Unification Phase 3c. Lazy-fetched per-skill
   * versions + consumers populate the slideout's two new tabs. We fetch
   * on demand (when the operator switches to the relevant tab) so the
   * Overview / Inputs / Guardrails / Evals / Platform tabs that don't
   * need this data stay snappy. Cached by skill id — re-opening the
   * same skill doesn't re-fetch. */
  const [versionsBySkillId,  setVersionsBySkillId]  = useState({}); // id → array
  const [consumersBySkillId, setConsumersBySkillId] = useState({}); // id → {totalConsumers, byVersion}
  const [versionsLoading,    setVersionsLoading]    = useState(false);
  const [consumersLoading,   setConsumersLoading]   = useState(false);

  /* P7J-RUN-037 — Skill Unification Phase 3d. Lifecycle mutation state.
   * `lifecycleAction` holds the pending action ('DEPRECATE' | 'RETIRE' |
   * 'RESTORE') so the confirm modal can render the right copy and call
   * the right state transition. `lifecycleReason` is the operator-supplied
   * reason (optional for DEPRECATE, required for RETIRE per UX). */
  const [lifecycleAction,  setLifecycleAction]  = useState(null);
  const [lifecycleReason,  setLifecycleReason]  = useState('');
  const [lifecycleSubmitting, setLifecycleSubmitting] = useState(false);

  const openLifecycleAction = (action) => {
    setLifecycleAction(action);
    setLifecycleReason('');
  };
  const closeLifecycleAction = () => {
    setLifecycleAction(null);
    setLifecycleReason('');
  };

  const handleLifecycleSubmit = async () => {
    if (!selected || !lifecycleAction) return;
    const newState =
        lifecycleAction === 'DEPRECATE' ? 'DEPRECATED'
      : lifecycleAction === 'RETIRE'    ? 'RETIRED'
      : lifecycleAction === 'RESTORE'   ? 'ACTIVE'
      : null;
    if (!newState) return;
    // RETIRE requires a reason — protect the audit chain from blank entries.
    if (lifecycleAction === 'RETIRE' && !lifecycleReason.trim()) {
      addToast({ tone: 'warning', title: 'Reason required',
        description: 'Retirement is terminal; supply a reason so the audit chain captures the rationale.' });
      return;
    }
    setLifecycleSubmitting(true);
    try {
      const res = await api.changeSkillRegistryLifecycle?.(selected.id, {
        newState,
        reason: lifecycleReason.trim() || null,
      });
      if (res?.ok === false) {
        addToast({ tone: 'danger', title: 'Lifecycle change failed',
          description: res.error || `HTTP ${res.status || '?'}` });
        return;
      }
      addToast({ tone: 'success',
        title: lifecycleAction === 'DEPRECATE' ? 'Marked deprecated'
             : lifecycleAction === 'RETIRE'    ? 'Retired'
             : 'Restored to ACTIVE',
        description: selected.displayName || selected.name });
      // Update the selected row in-place so the slideout's header
      // pills reflect the new state without re-fetch.
      const updated = res?.data || res;
      if (updated && typeof updated === 'object') {
        setSelected({ ...selected, ...updated });
        // Replace the row in the marketplace list so the table column
        // pill / row visibility (RETIRED hidden by default) updates.
        setMarketplace((prev) => prev.map(s => s.id === selected.id ? { ...s, ...updated } : s));
      } else {
        reload();
      }
      closeLifecycleAction();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Lifecycle change failed',
        description: err?.message || String(err) });
    } finally {
      setLifecycleSubmitting(false);
    }
  };

  /* P7J-RUN-037 — Skill Unification Phase 3d. When the lifecycle confirm
   * modal opens, eagerly fetch the consumers list (if not already cached
   * by the Consumers tab) so the cascade-impact preview is always populated
   * before the operator commits to a state transition. Empty cache shows
   * a brief "Loading…" line; non-empty cache renders immediately. */
  useEffect(() => {
    if (!lifecycleAction || !selected) return;
    if (consumersBySkillId[selected.id]) return;
    setConsumersLoading(true);
    api.getSkillRegistryConsumers?.(selected.id)
      .then((res) => {
        const data = res?.data || res || { totalConsumers: 0, byVersion: [] };
        setConsumersBySkillId((prev) => ({ ...prev, [selected.id]: data }));
      })
      .catch(() => setConsumersBySkillId((prev) => ({ ...prev, [selected.id]: { totalConsumers: 0, byVersion: [] } })))
      .finally(() => setConsumersLoading(false));
  }, [lifecycleAction, selected, consumersBySkillId]);

  useEffect(() => {
    if (!selected) return;
    if (slideoutTab === 'versions' && !versionsBySkillId[selected.id]) {
      setVersionsLoading(true);
      api.getSkillRegistryVersions?.(selected.id)
        .then((res) => {
          const list = Array.isArray(res?.data?.versions) ? res.data.versions
                     : Array.isArray(res?.versions) ? res.versions : [];
          setVersionsBySkillId((prev) => ({ ...prev, [selected.id]: list }));
        })
        .catch(() => setVersionsBySkillId((prev) => ({ ...prev, [selected.id]: [] })))
        .finally(() => setVersionsLoading(false));
    }
    if (slideoutTab === 'consumers' && !consumersBySkillId[selected.id]) {
      setConsumersLoading(true);
      api.getSkillRegistryConsumers?.(selected.id)
        .then((res) => {
          const data = res?.data || res || { totalConsumers: 0, byVersion: [] };
          setConsumersBySkillId((prev) => ({ ...prev, [selected.id]: data }));
        })
        .catch(() => setConsumersBySkillId((prev) => ({ ...prev, [selected.id]: { totalConsumers: 0, byVersion: [] } })))
        .finally(() => setConsumersLoading(false));
    }
  }, [selected, slideoutTab, versionsBySkillId, consumersBySkillId]);

  /* Actions */
  /* G225 — Install requires an agent binding (api.installSkill expects
   * { agentId }). Earlier code called it without agentId and the request
   * silently no-op'd. Now we open the agent picker first; the picker then
   * fires the bind call with both ids. */
  const [installPickerOpen, setInstallPickerOpen] = useState(false);
  const [installCandidate, setInstallCandidate] = useState(null);
  const [installPickerAgents, setInstallPickerAgents] = useState([]);
  const [installPickerLoading, setInstallPickerLoading] = useState(false);
  const [installPickerFilter, setInstallPickerFilter] = useState('');
  const [installSubmitting, setInstallSubmitting] = useState(false);

  const handleInstall = async (s) => {
    setInstallCandidate(s);
    setInstallPickerFilter('');
    setInstallPickerOpen(true);
    setInstallPickerLoading(true);
    try {
      const res = await api.getAgents?.({});
      const arr = Array.isArray(res?.data?.items) ? res.data.items
                : Array.isArray(res?.data) ? res.data
                : Array.isArray(res?.items) ? res.items
                : Array.isArray(res) ? res : [];
      setInstallPickerAgents(arr);
    } catch { setInstallPickerAgents([]); }
    finally { setInstallPickerLoading(false); }
  };

  const confirmInstall = async (agent) => {
    if (!installCandidate || !agent?.id) return;
    setInstallSubmitting(true);
    try {
      const res = await api.installSkill?.(installCandidate.id, { agentId: agent.id });
      if (!res || res.ok === false) {
        addToast({ tone: 'danger', title: 'Install failed', description: res?.error || res?.data?.error || `HTTP ${res?.status || '?'}` });
        return;
      }
      addToast({
        tone: 'success',
        title: `Installed "${installCandidate.displayName || installCandidate.name}"`,
        description: `Now bound to ${agent.name}.`,
      });
      setInstallPickerOpen(false);
      setInstallCandidate(null);
      reload();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Install failed', description: err?.message || String(err) });
    } finally {
      setInstallSubmitting(false);
    }
  };
  const handleDisable = async (s) => {
    try {
      const res = await api.disableSkill?.(s.id);
      if (res?.ok) {
        addToast({ tone: 'info', title: `Disabled "${s.displayName || s.name}"` });
        reload();
      } else {
        addToast({ tone: 'danger', title: 'Disable failed', description: res?.error || 'Unknown error' });
      }
    } catch (err) {
      addToast({ tone: 'danger', title: 'Disable failed', description: err?.message });
    }
  };

  /* KPIs */
  const kpis = useMemo(() => ({
    total:        marketplace.length,
    installed:    installedIds.size,
    workflowSkills: marketplace.filter(s => s.kind === 'workflow_skill').length,
    avgEval:      (() => {
      const scores = marketplace
        .map(s => s.evalScore == null ? null : (Number(s.evalScore) > 1 ? Number(s.evalScore) : Number(s.evalScore) * 100))
        .filter(n => Number.isFinite(n));
      if (!scores.length) return null;
      return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
    })(),
  }), [marketplace, installedIds]);

  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1500px] mx-auto">
      <header className="flex items-start gap-4 mb-5">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight m-0">Skill Center</h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            {kpis.total} skills · {kpis.installed} installed · {searched.length} match {searchQuery ? 'search' : 'filters'}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>Refresh</Button>
          <Button variant="secondary" size="sm" onClick={cycleDensity} title={`Row density: ${density}`}>
            Density: {density.charAt(0).toUpperCase() + density.slice(1)}
          </Button>
          <Button variant="primary" size="sm" iconLeft={<Plus size={13} />}
            onClick={openAddSkill}>
            Add Skill
          </Button>
        </div>
      </header>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Total skills"     value={kpis.total} tone="cyan" />
        <MetricCard label="Installed"        value={kpis.installed} tone="success"
          delta={{ value: `${kpis.total ? Math.round((kpis.installed / kpis.total) * 100) : 0}% adoption`, direction: 'flat', intent: 'neutral' }} />
        <MetricCard label="Workflow skills"  value={kpis.workflowSkills} tone="trust" />
        <MetricCard label="Avg eval score"   value={kpis.avgEval == null ? 'No data' : `${kpis.avgEval}`} tone="action" />
      </div>

      {/* Search bar */}
      <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-3 py-2 mb-4 flex items-center gap-2">
        <Search size={14} className="text-v2-ink-muted" />
        <input
          type="text"
          placeholder="Search skills by name, description, kind, or category…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 bg-transparent text-[13px] focus:outline-none text-v2-ink placeholder-v2-ink-muted"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="text-v2-ink-muted hover:text-v2-ink">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Active filter chips */}
      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map((c) => (
            <button key={`${c.groupKey}:${c.optionKey}`}
              onClick={() => toggleFacet(c.groupKey, c.optionKey, false)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white">
              {c.label} ×
            </button>
          ))}
          <button onClick={clearFacets} className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">Clear all</button>
        </div>
      )}

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail
          groups={facetGroupsWithCounts}
          onToggle={toggleFacet}
          ariaLabel="Skill filters"
        />

        {searched.length === 0 && !loading ? (
          <EmptyState
            icon={<Sparkles size={20} />}
            title={searchQuery ? 'No skills match your search' : 'No skills match the filters'}
            description={searchQuery
              ? `Try a different search term or clear the search.`
              : activeFilterChips.length > 0
                ? 'Adjust filters or clear them to see more.'
                : 'No skills available yet.'}
            primaryAction={
              searchQuery
                ? <Button variant="primary" onClick={() => setSearchQuery('')}>Clear search</Button>
                : activeFilterChips.length > 0
                  ? <Button variant="primary" onClick={clearFacets}>Clear filters</Button>
                  : null
            }
          />
        ) : (
          <Table
            ariaLabel="Skills"
            density={density}
            columns={[
              { key: 'name', header: 'Skill', sortable: true, render: (s) => (
                <div>
                  <div className="font-semibold text-v2-ink">{s.displayName || s.name}</div>
                  <div className="text-[10.5px] text-v2-ink-muted">{(s.description || '').slice(0, 70)}{(s.description || '').length > 70 ? '…' : ''}</div>
                </div>
              )},
              /* P7J-RUN-037 — Origin pill. Renders for every row;
               * defaults legacy rows that pre-date V231 to MARKETPLACE
               * (which matches the V231 column default the migration set). */
              { key: 'origin', header: 'Origin', render: (s) => {
                const o = s.origin || 'MARKETPLACE';
                return (
                  <Pill tone={ORIGIN_TONE[o] || 'muted'} size="sm" dot>
                    {ORIGIN_LABEL[o] || o.toLowerCase().replace(/_/g, ' ')}
                  </Pill>
                );
              }},
              { key: 'kind', header: 'Kind', render: (s) => (
                <Pill tone={KIND_TONE[s.kind] || 'muted'} size="sm">{(s.kind || 'unknown').replace(/_/g, ' ')}</Pill>
              )},
              /* P7J-RUN-037 — Registry version chip. Tiny monospace
               * "v1" / "v2" so operators can see at a glance which
               * version this row is. Null-safe: legacy rows show "v1". */
              { key: 'registryVersion', header: 'Ver', render: (s) => (
                <code className="text-[10.5px] px-1.5 py-0.5 rounded bg-v2-bg-muted text-v2-ink-muted font-mono-v2">
                  v{s.registryVersion ?? 1}
                </code>
              )},
              /* P7J-RUN-037 — Lifecycle state pill. Hidden for ACTIVE
               * (the common case) to keep the row uncluttered; rendered
               * only when the state is something an operator should
               * notice (DEPRECATED / RETIRED / DRAFT). */
              { key: 'lifecycleState', header: 'Lifecycle', render: (s) => {
                const ls = s.lifecycleState || 'ACTIVE';
                if (ls === 'ACTIVE') return <span className="text-[11px] text-v2-ink-muted">active</span>;
                return (
                  <Pill tone={LIFECYCLE_TONE[ls] || 'muted'} size="sm" dot>
                    {ls.toLowerCase()}
                  </Pill>
                );
              }},
              { key: 'risk', header: 'Risk', render: (s) => {
                const r = (s.riskLevel || s.risk || 'low').toLowerCase();
                return <Pill tone={RISK_TONE[r] || 'muted'} size="sm" dot>{r}</Pill>;
              }},
              { key: 'evals', header: 'Evals', render: (s) => (
                <span className="text-[12px] text-v2-ink">
                  {s.evalCount || 0} <span className="text-v2-ink-muted">·</span>{' '}
                  {s.guardrailCount || 0}g
                </span>
              )},
              { key: 'status', header: 'Status', render: (s) => (
                installedIds.has(s.id)
                  ? <Pill tone="success" size="sm" dot>Installed</Pill>
                  : <Pill tone="muted" size="sm">Available</Pill>
              )},
              { key: 'actions', header: '', align: 'right', render: (s) => (
                <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                  {installedIds.has(s.id) ? (
                    <Button variant="ghost" size="sm" className="!text-v2-danger hover:!bg-v2-danger-soft"
                      onClick={() => handleDisable(s)} title="Disable">
                      <Trash2 size={13} />
                    </Button>
                  ) : (
                    <Button variant="primary" size="sm" onClick={() => handleInstall(s)}>
                      Install
                    </Button>
                  )}
                </div>
              )},
            ]}
            rows={visibleRows}
            rowKey={(s) => s.id}
            onRowClick={openSlideout}
          />
        )}
      </div>

      {searched.length > 0 && (
        <div className="grid gap-5 mt-3 grid-cols-1 lg:grid-cols-[240px_1fr]">
          <div />
          <Pagination
            totalItems={searched.length}
            page={pg.page}
            pageSize={pg.pageSize}
            onPageChange={pg.setPage}
            onPageSizeChange={pg.setPageSize}
            noun="skills"
          />
        </div>
      )}

      <Slideout
        isOpen={!!selected}
        onClose={closeSlideout}
        ariaLabel={selected ? `${selected.displayName || selected.name} detail` : 'Skill detail'}
        width={520}
        tabs={[
          { id: 'overview',   label: 'Overview' },
          { id: 'inputs',     label: 'Inputs' },
          { id: 'guardrails', label: 'Guardrails', badge: selected?.guardrailCount || undefined },
          { id: 'evals',      label: 'Evals',      badge: selected?.evalCount || undefined },
          { id: 'platform',   label: 'Platform' },
          /* P7J-RUN-037 — Skill Unification Phase 3c. Versions tab shows
           * the recursive parent_entry_id chain as a timeline. Consumers
           * tab shows agents bound to this entry, grouped by version_pin
           * (cascade-impact preview). Tabs lazy-load on first activation. */
          { id: 'versions',   label: 'Versions',   badge: (versionsBySkillId[selected?.id || ''] || []).length || undefined },
          { id: 'consumers',  label: 'Consumers',  badge: consumersBySkillId[selected?.id || '']?.totalConsumers || undefined },
        ]}
        activeTabId={slideoutTab}
        onTabChange={setSlideoutTab}
        header={selected && (
          <div>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {/* P7J-RUN-037 — Origin pill in the slideout header so the
                * provenance is visible without going to the Versions tab. */}
              {selected.origin && (
                <Pill tone={ORIGIN_TONE[selected.origin] || 'muted'} size="sm" dot>
                  {ORIGIN_LABEL[selected.origin] || selected.origin.toLowerCase().replace(/_/g, ' ')}
                </Pill>
              )}
              <Pill tone={KIND_TONE[selected.kind] || 'muted'} size="sm">
                {(selected.kind || 'unknown').replace(/_/g, ' ')}
              </Pill>
              <Pill tone={RISK_TONE[(selected.riskLevel || selected.risk || 'low').toLowerCase()] || 'muted'} size="sm" dot>
                {(selected.riskLevel || selected.risk || 'low').toLowerCase()} risk
              </Pill>
              {/* P7J-RUN-037 — Lifecycle pill in header — shown for any
                * non-ACTIVE state so operators see drift / risk at a glance. */}
              {selected.lifecycleState && selected.lifecycleState !== 'ACTIVE' && (
                <Pill tone={LIFECYCLE_TONE[selected.lifecycleState] || 'muted'} size="sm" dot>
                  {selected.lifecycleState.toLowerCase()}
                </Pill>
              )}
            </div>
            <h2 className="text-[18px] font-bold tracking-tight m-0">{selected.displayName || selected.name}</h2>
            <p className="text-[12px] text-v2-ink-secondary">v{selected.version || '1.0.0'}{selected.parentName && ` · via ${selected.parentName}`}</p>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {installedIds.has(selected.id) ? (
                <>
                  <Button variant="primary" size="sm" iconLeft={<Settings size={13} />}
                    onClick={() => {
                      /* G198 — Skills are owned by agents; "Configure" means
                       * "open the owning agent's detail page Build tab where
                       * Edit/Push/Delete actions live (G192)". */
                      if (selected.agentId) {
                        navigate(`/agents/${selected.agentId}`);
                      } else {
                        addToast({ tone: 'warning', title: 'Configure unavailable', description: 'This skill has no owning agent recorded.' });
                      }
                    }}>
                    Configure
                  </Button>
                  <Button variant="ghost" size="sm" className="!text-v2-danger"
                    onClick={() => handleDisable(selected)}>
                    Disable
                  </Button>
                </>
              ) : (
                <Button variant="primary" size="sm" iconLeft={<Plus size={13} />}
                  onClick={() => handleInstall(selected)}>
                  Install
                </Button>
              )}
              {/* P7J-RUN-037 — Skill Unification Phase 3d. Lifecycle action
                * buttons. Rendered conditionally on current state so the
                * operator only sees valid transitions:
                *   ACTIVE      → "Mark deprecated"  + "Retire"
                *   DEPRECATED  → "Restore"          + "Retire"
                *   RETIRED     → terminal (no buttons)
                *   DRAFT       → no lifecycle UI yet (Draft is author-time)
                * Each click opens the confirm modal which shows the cascade
                * impact preview from the already-loaded consumers cache. */}
              {selected.lifecycleState === 'ACTIVE' && (
                <>
                  <Button variant="ghost" size="sm" iconLeft={<Clock size={13} />}
                    className="!text-v2-warning hover:!bg-v2-warning-soft"
                    onClick={() => openLifecycleAction('DEPRECATE')}>
                    Mark deprecated
                  </Button>
                  <Button variant="ghost" size="sm" iconLeft={<AlertTriangle size={13} />}
                    className="!text-v2-danger hover:!bg-v2-danger-soft"
                    onClick={() => openLifecycleAction('RETIRE')}>
                    Retire
                  </Button>
                </>
              )}
              {selected.lifecycleState === 'DEPRECATED' && (
                <>
                  <Button variant="ghost" size="sm" iconLeft={<RotateCcw size={13} />}
                    className="!text-v2-success hover:!bg-v2-success-soft"
                    onClick={() => openLifecycleAction('RESTORE')}>
                    Restore
                  </Button>
                  <Button variant="ghost" size="sm" iconLeft={<AlertTriangle size={13} />}
                    className="!text-v2-danger hover:!bg-v2-danger-soft"
                    onClick={() => openLifecycleAction('RETIRE')}>
                    Retire
                  </Button>
                </>
              )}
              {selected.lifecycleState === 'RETIRED' && (
                <span className="text-[11px] text-v2-ink-muted italic">Retired — terminal state.</span>
              )}
            </div>
          </div>
        )}
      >
        {selected && slideoutTab === 'overview' && (
          <Card padding="sm">
            <Card.Header title="Overview" />
            <Card.Body>
              <p className="text-[12.5px] text-v2-ink leading-relaxed mb-3">{selected.description || 'No description available.'}</p>
              <dl className="grid grid-cols-2 gap-3 text-[12px]">
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Kind</dt><dd className="text-v2-ink mt-0.5">{(selected.kind || '—').replace(/_/g, ' ')}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Category</dt><dd className="text-v2-ink mt-0.5">{selected.category || '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Eval score</dt><dd className="text-v2-ink mt-0.5">{(() => { const v = Number(selected.evalScore); if (!Number.isFinite(v)) return 'No eval'; return v <= 1 ? Math.round(v * 100) : Math.round(v); })()}</dd></div>
              </dl>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'inputs' && (
          <Card padding="sm">
            <Card.Header title="Input parameters" />
            <Card.Body>
              {Array.isArray(selected.inputs) && selected.inputs.length > 0 ? (
                <ul className="space-y-2 text-[12.5px]">
                  {selected.inputs.map((p, i) => (
                    <li key={i} className="border-l-2 border-v2-border-subtle pl-3">
                      <div className="font-semibold text-v2-ink font-mono-v2 text-[12px]">{p.name}</div>
                      <div className="text-[11.5px] text-v2-ink-muted">{p.type || 'any'}{p.required && ' · required'}</div>
                      {p.description && <div className="text-[12px] text-v2-ink-secondary mt-1">{p.description}</div>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-v2-ink-muted text-[12.5px]">No input parameters declared.</p>
              )}
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'guardrails' && (
          <Card padding="sm">
            <Card.Header title={`Guardrails · ${selected.guardrailCount || 0}`} />
            <Card.Body>
              {(selected.guardrails || []).length > 0 ? (
                <ul className="space-y-2 text-[12.5px]">
                  {(selected.guardrails || []).map((g, i) => (
                    <li key={i} className="flex items-baseline gap-2">
                      <Pill tone="warning" size="sm">{g.type || 'guard'}</Pill>
                      <span className="text-v2-ink">{g.description || g.name || '—'}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-v2-ink-muted text-[12.5px]">No explicit guardrails — falls back to platform defaults.</p>
              )}
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'evals' && (
          <Card padding="sm">
            <Card.Header title={`Evals · ${selected.evalCount || 0}`} />
            <Card.Body>
              <p className="text-v2-ink-muted text-[12.5px]">
                Open this skill's owning agent to inspect eval runs and generate / re-run suites for it.
              </p>
              <Button variant="ghost" size="sm" className="mt-2"
                onClick={() => {
                  /* G198 — Evals live on the owning agent's detail page Build
                   * tab. Per-row Generate eval + agent-level Generate evals
                   * gate remediation handle the rest. */
                  if (selected.agentId) navigate(`/agents/${selected.agentId}`);
                  else navigate('/agents');
                }}>
                Open owning agent →
              </Button>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'platform' && (
          <Card padding="sm">
            <Card.Header title="Platform metadata" />
            <Card.Body>
              <pre className="text-[10.5px] font-mono-v2 text-v2-ink-secondary bg-v2-muted rounded-v2-sm p-3 max-h-[400px] overflow-auto">
                {JSON.stringify({
                  id: selected.id,
                  kind: selected.kind,
                  version: selected.version,
                  parent: selected.parentName,
                  manifestSource: selected.manifestSource,
                }, null, 2)}
              </pre>
            </Card.Body>
          </Card>
        )}

        {/* P7J-RUN-037 — Skill Unification Phase 3c. Versions tab —
          * recursive parent_entry_id chain rendered as a timeline. */}
        {selected && slideoutTab === 'versions' && (
          <Card padding="sm">
            <Card.Header title="Version chain" />
            <Card.Body>
              {versionsLoading && (
                <p className="text-[12px] text-v2-ink-muted italic">Loading version history…</p>
              )}
              {!versionsLoading && (versionsBySkillId[selected.id] || []).length === 0 && (
                <p className="text-[12px] text-v2-ink-muted">
                  Only one version on file. New versions appear here as soon as this skill is forked
                  via the Edit / Push flow.
                </p>
              )}
              {!versionsLoading && (versionsBySkillId[selected.id] || []).length > 0 && (
                <ol className="space-y-2.5 relative pl-5 before:content-[''] before:absolute before:left-1.5 before:top-2 before:bottom-2 before:w-px before:bg-v2-border-subtle">
                  {(versionsBySkillId[selected.id] || []).map((v) => {
                    const isCurrent = v.id === selected.id;
                    return (
                      <li key={v.id} className="relative text-[12px]">
                        <span
                          className={`absolute -left-[15px] top-1 inline-block w-2.5 h-2.5 rounded-full border-2 ${
                            isCurrent
                              ? 'bg-v2-action border-v2-action'
                              : v.lifecycleState === 'DEPRECATED'
                                ? 'bg-white border-v2-warning'
                                : 'bg-white border-v2-border'
                          }`}
                        />
                        <div className="flex items-center gap-2 flex-wrap">
                          <strong className="text-v2-ink">v{v.registryVersion ?? 1}</strong>
                          {isCurrent && <Pill tone="action" size="sm">current</Pill>}
                          {v.lifecycleState && v.lifecycleState !== 'ACTIVE' && (
                            <Pill tone={LIFECYCLE_TONE[v.lifecycleState] || 'muted'} size="sm" dot>
                              {v.lifecycleState.toLowerCase()}
                            </Pill>
                          )}
                          <span className="text-[10.5px] text-v2-ink-muted font-mono-v2">{v.id}</span>
                        </div>
                        <div className="text-[11px] text-v2-ink-muted mt-0.5">
                          {v.displayName || v.name}
                          {v.updatedAt && (<> · updated {new Date(v.updatedAt).toLocaleString()}</>)}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </Card.Body>
          </Card>
        )}

        {/* P7J-RUN-037 — Skill Unification Phase 3c. Consumers tab —
          * agents bound to this entry, grouped by version_pin so the
          * operator can see the cascade-impact distribution before
          * any deprecate / retire decision. */}
        {selected && slideoutTab === 'consumers' && (
          <Card padding="sm">
            <Card.Header
              title={`Consumers · ${consumersBySkillId[selected.id]?.totalConsumers || 0}`}
            />
            <Card.Body>
              {consumersLoading && (
                <p className="text-[12px] text-v2-ink-muted italic">Loading consumers…</p>
              )}
              {!consumersLoading && (consumersBySkillId[selected.id]?.totalConsumers || 0) === 0 && (
                <p className="text-[12px] text-v2-ink-muted">
                  No agents currently bind to this entry. New consumers appear here as soon as the
                  skill is added to an agent via <em>+ New skill</em>, <em>Add existing skill</em>,
                  or <em>Discover capabilities</em>.
                </p>
              )}
              {!consumersLoading && (consumersBySkillId[selected.id]?.byVersion || []).map(group => (
                <div key={group.version} className="mb-4 last:mb-0">
                  <div className="flex items-baseline gap-2 mb-1.5">
                    <strong className="text-[12.5px] text-v2-ink">v{group.version}</strong>
                    <span className="text-[11px] text-v2-ink-muted">
                      {group.count} agent{group.count === 1 ? '' : 's'}
                      {group.autoTrackingCount > 0 && (
                        <> · <span className="text-v2-action">{group.autoTrackingCount} auto-tracking latest</span></>
                      )}
                    </span>
                  </div>
                  <ul className="space-y-1.5">
                    {(group.items || []).map(c => (
                      <li key={c.skillId} className="flex items-center gap-2 text-[11.5px]">
                        <Pill
                          tone={c.agentArchetype === 'ExteLet' ? 'cyan'
                              : c.agentArchetype === 'AutoLet' ? 'trust'
                              : 'muted'}
                          size="sm"
                        >
                          {c.agentArchetype || 'Let'}
                        </Pill>
                        <button
                          type="button"
                          className="v2-focus-ring text-v2-action hover:underline truncate"
                          onClick={() => { closeSlideout(); navigate(`/agents/${c.agentId}`); }}
                          title="Open this agent's detail page"
                        >
                          {c.agentName || c.agentId}
                        </button>
                        {c.pinToLatest && (
                          <span className="text-[10px] text-v2-action-soft-strong">· auto-track</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </Card.Body>
          </Card>
        )}
      </Slideout>

      {/* G198 — Add Skill agent picker. Skills live on an agent; this modal
       * lets the operator pick which agent to add the new skill to, then
       * deep-links to that agent's detail page with ?intent=add-skill so
       * the skill-create modal auto-opens there. */}
      <Modal
        isOpen={addSkillOpen}
        onClose={() => setAddSkillOpen(false)}
        size="md"
        title="Add a skill — pick the agent"
        footer={<Button variant="ghost" onClick={() => setAddSkillOpen(false)}>Cancel</Button>}
      >
        <div className="space-y-3 text-[12.5px]">
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-v2-ink-muted" />
            <input
              type="text"
              autoFocus
              value={addSkillFilter}
              onChange={(e) => setAddSkillFilter(e.target.value)}
              placeholder="Search agents by name or category"
              className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm pl-7 pr-2 py-1.5 text-[12.5px]"
            />
          </div>
          <div className="max-h-[420px] overflow-y-auto border border-v2-border-subtle rounded-v2-sm">
            {addSkillLoading ? (
              <div className="p-3 text-v2-ink-muted">Loading agents…</div>
            ) : (() => {
              const q = addSkillFilter.trim().toLowerCase();
              const filtered = addSkillAgents.filter((a) => !q
                || (a.name || '').toLowerCase().includes(q)
                || (a.category || a.type || '').toLowerCase().includes(q));
              if (filtered.length === 0) {
                return <div className="p-3 text-v2-ink-muted">No matching agents.</div>;
              }
              return filtered.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => {
                    navigate(`/agents/${a.id}?intent=add-skill`);
                    setAddSkillOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 border-b border-v2-border-subtle last:border-b-0 hover:bg-v2-muted transition-colors duration-v2"
                >
                  <div className="text-[12.5px] font-semibold text-v2-ink">{a.name}</div>
                  <div className="text-[11px] text-v2-ink-muted">
                    {(a.archetype || 'Let')} · {a.category || a.type || 'general'} · phase {a.phase || 'build'}
                  </div>
                </button>
              ));
            })()}
          </div>
          <div className="text-[11px] text-v2-ink-muted">
            New skill is created on the selected agent's Build tab. You can also reach it from any agent detail page.
          </div>
        </div>
      </Modal>

      {/* G225 — Install Skill agent picker. Skills install ONTO an agent;
        * the bind requires agentId. Without this modal the Install button
        * silently no-op'd. */}
      <Modal
        isOpen={installPickerOpen}
        onClose={() => !installSubmitting && setInstallPickerOpen(false)}
        size="md"
        title={installCandidate
          ? `Install "${installCandidate.displayName || installCandidate.name}" — pick the agent`
          : 'Install skill — pick the agent'}
        footer={<Button variant="ghost" onClick={() => setInstallPickerOpen(false)} disabled={installSubmitting}>Cancel</Button>}
      >
        <div className="space-y-3 text-[12.5px]">
          <p className="text-v2-ink-secondary leading-relaxed">
            Choose the agent that should host this skill. The skill will be added to the agent's
            Build tab; you can fine-tune autonomy, guardrails, and connector bindings there.
          </p>
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-v2-ink-muted" />
            <input
              type="text"
              autoFocus
              value={installPickerFilter}
              onChange={(e) => setInstallPickerFilter(e.target.value)}
              placeholder="Search agents by name or category"
              className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm pl-7 pr-2 py-1.5 text-[12.5px]"
              disabled={installSubmitting}
            />
          </div>
          <div className="max-h-[420px] overflow-y-auto border border-v2-border-subtle rounded-v2-sm">
            {installPickerLoading ? (
              <div className="p-3 text-v2-ink-muted">Loading agents…</div>
            ) : (() => {
              const q = installPickerFilter.trim().toLowerCase();
              const filtered = installPickerAgents.filter((a) => !q
                || (a.name || '').toLowerCase().includes(q)
                || (a.category || a.type || '').toLowerCase().includes(q));
              if (filtered.length === 0) {
                return <div className="p-3 text-v2-ink-muted">No matching agents.</div>;
              }
              return filtered.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => confirmInstall(a)}
                  disabled={installSubmitting}
                  className="w-full text-left px-3 py-2 border-b border-v2-border-subtle last:border-b-0 hover:bg-v2-muted transition-colors duration-v2 disabled:opacity-50 disabled:cursor-wait"
                >
                  <div className="text-[12.5px] font-semibold text-v2-ink">{a.name}</div>
                  <div className="text-[11px] text-v2-ink-muted">
                    {(a.archetype || 'Let')} · {a.category || a.type || 'general'} · phase {(a.phase || 'build').toLowerCase()}
                  </div>
                </button>
              ));
            })()}
          </div>
          {installSubmitting && (
            <div className="text-[11.5px] text-v2-action-strong">Installing…</div>
          )}
        </div>
      </Modal>

      {/* P7J-RUN-037 — Skill Unification Phase 3d.1. Lifecycle confirm modal.
        * Renders the cascade-impact preview (consumer count + per-version
        * breakdown from the lazy-loaded consumersBySkillId cache) so the
        * operator can see how many agents will be affected before
        * committing. RETIRE requires a reason; the action is terminal and
        * cannot be undone. DEPRECATE is reversible via RESTORE.
        * The action verb + tone shifts to match the chosen transition. */}
      <Modal
        isOpen={!!lifecycleAction && !!selected}
        onClose={() => !lifecycleSubmitting && closeLifecycleAction()}
        size="md"
        title={
          lifecycleAction === 'DEPRECATE' ? `Mark "${selected?.displayName || selected?.name || 'skill'}" as deprecated`
          : lifecycleAction === 'RETIRE'  ? `Retire "${selected?.displayName || selected?.name || 'skill'}"`
          : lifecycleAction === 'RESTORE' ? `Restore "${selected?.displayName || selected?.name || 'skill'}" to ACTIVE`
          : 'Lifecycle change'
        }
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={closeLifecycleAction} disabled={lifecycleSubmitting}>
              Cancel
            </Button>
            {/* Button v2 supports primary | secondary | ghost | danger.
              * RETIRE → danger (terminal, irreversible).
              * DEPRECATE / RESTORE → primary (deep teal CTA). The
              * preceding banner already carries the amber / green tone. */}
            <Button
              variant={lifecycleAction === 'RETIRE' ? 'danger' : 'primary'}
              onClick={handleLifecycleSubmit}
              disabled={lifecycleSubmitting
                || (lifecycleAction === 'RETIRE' && !lifecycleReason.trim())}>
              {lifecycleSubmitting ? 'Submitting…'
                : lifecycleAction === 'DEPRECATE' ? 'Mark deprecated'
                : lifecycleAction === 'RETIRE'    ? 'Retire permanently'
                : lifecycleAction === 'RESTORE'   ? 'Restore'
                : 'Confirm'}
            </Button>
          </div>
        }
      >
        {selected && (
          <div className="space-y-4 text-[12.5px]">
            {/* Transition explainer */}
            <div className={`border rounded-v2-sm p-3 ${
              lifecycleAction === 'RETIRE' ? 'border-v2-danger-soft bg-v2-danger-soft/40'
              : lifecycleAction === 'DEPRECATE' ? 'border-v2-warning-soft bg-v2-warning-soft/40'
              : 'border-v2-success-soft bg-v2-success-soft/40'
            }`}>
              <div className="flex items-baseline gap-2 mb-1">
                <strong className="text-v2-ink">
                  {selected.lifecycleState || 'ACTIVE'} →{' '}
                  {lifecycleAction === 'DEPRECATE' ? 'DEPRECATED'
                   : lifecycleAction === 'RETIRE'    ? 'RETIRED'
                   : 'ACTIVE'}
                </strong>
                <span className="text-[10.5px] text-v2-ink-muted font-mono-v2">
                  v{selected.registryVersion ?? 1} · {selected.id}
                </span>
              </div>
              <p className="text-[12px] text-v2-ink-secondary leading-relaxed m-0">
                {lifecycleAction === 'DEPRECATE' && (
                  <>The skill stays installable for the 14-day grace window so existing consumers can migrate. New installs surface a deprecation banner. This is reversible — operators can <em>Restore</em> back to ACTIVE.</>
                )}
                {lifecycleAction === 'RETIRE' && (
                  <>The skill is removed from new-install pickers and existing bindings will refuse to execute (gate fails). <strong className="text-v2-danger">This is terminal and cannot be reversed.</strong> A reason is required so the audit chain captures the rationale.</>
                )}
                {lifecycleAction === 'RESTORE' && (
                  <>The skill returns to ACTIVE and is once again installable + executable. The deprecation banner is removed. No consumer rebinding is required.</>
                )}
              </p>
            </div>

            {/* Cascade impact preview */}
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1.5">
                Cascade impact
              </div>
              {consumersLoading && !consumersBySkillId[selected.id] && (
                <p className="text-[12px] text-v2-ink-muted italic">Loading consumer list…</p>
              )}
              {!consumersLoading && consumersBySkillId[selected.id] && (() => {
                const data = consumersBySkillId[selected.id];
                const total = data.totalConsumers || 0;
                const groups = data.byVersion || [];
                if (total === 0) {
                  return (
                    <p className="text-[12px] text-v2-ink-secondary">
                      <strong className="text-v2-success">No agents currently bind to this entry.</strong>{' '}
                      Safe to {lifecycleAction === 'DEPRECATE' ? 'mark deprecated'
                              : lifecycleAction === 'RETIRE' ? 'retire'
                              : 'restore'} with no operational impact.
                    </p>
                  );
                }
                return (
                  <div>
                    <p className="text-[12px] text-v2-ink mb-2">
                      <strong className={lifecycleAction === 'RETIRE' ? 'text-v2-danger' : 'text-v2-warning'}>
                        {total} agent{total === 1 ? '' : 's'}
                      </strong> currently bind to this entry across {groups.length} version{groups.length === 1 ? '' : 's'}.
                      {lifecycleAction === 'RETIRE' && (
                        <> These bindings will <strong>fail enforcement</strong> after retirement.</>
                      )}
                      {lifecycleAction === 'DEPRECATE' && (
                        <> They will continue to execute during the 14-day grace window.</>
                      )}
                    </p>
                    <ul className="space-y-1 max-h-[180px] overflow-y-auto border border-v2-border-subtle rounded-v2-sm p-2">
                      {groups.map(g => (
                        <li key={g.version} className="text-[11.5px] flex items-baseline gap-2">
                          <strong className="text-v2-ink font-mono-v2">v{g.version}</strong>
                          <span className="text-v2-ink-secondary">
                            {g.count} agent{g.count === 1 ? '' : 's'}
                            {g.autoTrackingCount > 0 && (
                              <> · <span className="text-v2-action">{g.autoTrackingCount} auto-tracking</span></>
                            )}
                          </span>
                          <span className="text-v2-ink-muted truncate">
                            {(g.items || []).slice(0, 3).map(c => c.agentName || c.agentId).join(', ')}
                            {(g.items || []).length > 3 && (<> +{(g.items || []).length - 3} more</>)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })()}
            </div>

            {/* Reason field — required for RETIRE, optional for others */}
            <div>
              <label className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1.5">
                Reason{lifecycleAction === 'RETIRE' && <span className="text-v2-danger"> *</span>}
              </label>
              <textarea
                value={lifecycleReason}
                onChange={(e) => setLifecycleReason(e.target.value)}
                placeholder={
                  lifecycleAction === 'RETIRE'
                    ? 'Required — captured in the audit chain. e.g. "CVE-2026-1234 in upstream tool, no fix available."'
                    : 'Optional — appears in the audit chain and (for DEPRECATE) on the migration banner shown to consumers.'
                }
                rows={3}
                disabled={lifecycleSubmitting}
                className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-sm px-2.5 py-1.5 text-[12.5px] resize-y disabled:opacity-60"
              />
              {lifecycleAction === 'RETIRE' && !lifecycleReason.trim() && (
                <p className="text-[11px] text-v2-danger mt-1">A reason is required for permanent retirement.</p>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default function ResolvedSkillCenterPage(props) {
  const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
  const legacy = url?.searchParams?.get('legacy') === '1';
  if (legacy || !isRebirthSkillCenterEnabled()) return <SkillCenterPage {...props} />;
  return <SkillCenterPageV2Inner {...props} />;
}

export { SkillCenterPageV2Inner };
